import { motion } from "framer-motion";
import { FaShieldAlt, FaArrowRight, FaCheckCircle, FaStar, FaUsers, FaChartLine } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

export default function CTA() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/signup');
  };

  const handleScheduleDemo = () => {
    navigate('/support');
  };

  const benefits = [
    "Reduce operational costs by 40%",
    "Increase customer satisfaction by 60%",
    "Process claims 70% faster",
    "Access to 50K+ potential customers",
    "24/7 technical support",
    "ISO 27001 certified security"
  ];

  const stats = [
    { value: "100+", label: "Insurance Companies" },
    { value: "50K+", label: "Active Customers" },
    { value: "₵2M+", label: "Monthly Premiums" },
    { value: "99.9%", label: "Platform Uptime" }
  ];

  return (
    <section className="overflow-x-hidden relative px-4 py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Floating Elements */}
      <motion.div
        animate={{ 
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute top-20 right-20 w-32 h-32 rounded-full blur-3xl bg-blue-400/20"
      />
      <motion.div
        animate={{ 
          y: [0, 20, 0],
          rotate: [0, -5, 0]
        }}
        transition={{ 
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute bottom-20 left-20 w-40 h-40 rounded-full blur-3xl bg-indigo-400/20"
      />

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 mb-6 text-sm font-medium text-blue-200 rounded-full border backdrop-blur-sm bg-blue-800/50 border-blue-700/50"
            >
              <FaShieldAlt size={16} className="mr-2" />
              Join the Future of Insurance
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mb-6 text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl"
            >
              Transform Your Insurance Business Today
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mb-8 text-xl leading-relaxed text-blue-100"
            >
              Join hundreds of successful insurance providers who have revolutionized their operations with our cutting-edge platform. Experience the future of insurance technology.
            </motion.p>

            {/* Benefits List */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mb-8 space-y-3"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  className="flex items-center space-x-3 text-blue-100"
                >
                  <FaCheckCircle size={20} className="flex-shrink-0 text-emerald-400" />
                  <span className="text-lg">{benefit}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetStarted}
                className="flex justify-center items-center px-8 py-4 space-x-2 font-semibold text-blue-900 bg-white rounded-full shadow-lg transition-all duration-300 group hover:bg-blue-50 hover:shadow-xl"
              >
                <span>Get Started Now</span>
                <FaArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleScheduleDemo}
                className="px-8 py-4 font-semibold text-white rounded-full border-2 backdrop-blur-sm transition-all duration-300 border-white/30 hover:bg-white/10 hover:border-white/50"
              >
                Schedule Demo
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right Content - Stats & Testimonials */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="p-6 text-center rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20"
                >
                  <div className="mb-2 text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-blue-200">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Testimonial Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="p-6 rounded-2xl border backdrop-blur-sm bg-white/10 border-white/20"
            >
              <div className="flex items-center mb-4 space-x-1">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} size={16} className="text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="mb-4 italic text-blue-100">
                "TripSecure has revolutionized our claims processing. We've seen a 70% reduction in processing time and our customer satisfaction has never been higher."
              </p>
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-full">
                  <FaUsers size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold text-white">Sarah Johnson</div>
                  <div className="text-sm text-blue-200">CEO, SecureLife Insurance</div>
                </div>
              </div>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="flex justify-center items-center space-x-6 text-blue-200"
            >
              <div className="flex items-center space-x-2">
                <FaShieldAlt size={20} className="text-emerald-400" />
                <span className="text-sm">ISO 27001 Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <FaChartLine size={20} className="text-emerald-400" />
                <span className="text-sm">99.9% Uptime</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
